<template>
  <div class="agent-files-panel">
    <!-- Header -->
    <div class="panel-header">
      <button class="back-btn" @click="$emit('close')">
        <i class="el-icon-arrow-left"></i>
      </button>
      <div class="panel-title">
        <span class="agent-emoji">{{ agentEmoji }}</span>
        <span>{{ agentName }}</span>
        <span class="agent-id">{{ agentId }}</span>
      </div>
      <div class="panel-actions">
        <el-button v-if="dirty" size="mini" type="primary" :loading="saving" @click="save">
          <i class="el-icon-check"></i> 保存
        </el-button>
        <el-button v-if="dirty" size="mini" @click="revert">放弃</el-button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="panel-loading">
      <i class="el-icon-loading"></i> 加载中…
    </div>

    <!-- Error -->
    <div v-else-if="error" class="panel-error">
      <div>{{ error }}</div>
      <el-button size="mini" @click="loadFiles">重试</el-button>
    </div>

    <!-- Content -->
    <div v-else class="panel-body">
      <!-- File tabs -->
      <div class="file-tabs">
        <div
          v-for="f in files"
          :key="f.name"
          class="file-tab"
          :class="{ active: activeFile === f.name, missing: f.missing }"
          @click="selectFile(f)"
        >
          <span class="file-icon">{{ fileIcon(f.name) }}</span>
          <span class="file-name">{{ f.name }}</span>
          <span v-if="isDirty(f.name)" class="dirty-dot">●</span>
        </div>
      </div>

      <!-- Editor -->
      <div class="editor-area">
        <div v-if="!activeFile" class="editor-placeholder">
          <div class="placeholder-icon">📝</div>
          <div>选择左侧文件开始编辑</div>
        </div>
        <template v-else>
          <div class="editor-toolbar">
            <span class="editor-filename">{{ activeFile }}</span>
            <span v-if="activeFileMeta" class="editor-meta">
              {{ formatSize(activeFileMeta.size) }} · {{ formatTime(activeFileMeta.updatedAtMs) }}
            </span>
          </div>
          <textarea
            ref="editor"
            class="editor-textarea"
            :value="draft"
            @input="onInput"
            spellcheck="false"
            placeholder="文件内容为空，开始编写…"
          ></textarea>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
const FILE_ICONS = {
  'SOUL.md': '🧠',
  'AGENTS.md': '📋',
  'TOOLS.md': '🔧',
  'IDENTITY.md': '🪪',
  'USER.md': '👤',
  'MEMORY.md': '💾',
  'MEMORY.json': '💾',
  'HEARTBEAT.md': '💓',
  'BOOTSTRAP.md': '🚀',
};

export default {
  name: 'AgentFilesPanel',
  props: {
    gatewayClient: { type: Object, required: true },
    agentId: { type: String, required: true },
    agentName: { type: String, default: '' },
    agentEmoji: { type: String, default: '🤖' },
  },
  data() {
    return {
      loading: false,
      saving: false,
      error: null,
      files: [],
      activeFile: null,
      contents: {},   // name → server content
      drafts: {},     // name → local draft
    };
  },
  computed: {
    draft() {
      return this.drafts[this.activeFile] ?? '';
    },
    dirty() {
      return Object.keys(this.drafts).some(n => this.drafts[n] !== (this.contents[n] ?? ''));
    },
    activeFileMeta() {
      return this.files.find(f => f.name === this.activeFile);
    },
  },
  methods: {
    fileIcon(name) {
      return FILE_ICONS[name] || '📄';
    },
    isDirty(name) {
      return Object.hasOwn(this.drafts, name) && this.drafts[name] !== (this.contents[name] ?? '');
    },
    formatSize(bytes) {
      if (!bytes) return '';
      if (bytes < 1024) return bytes + ' B';
      return (bytes / 1024).toFixed(1) + ' KB';
    },
    formatTime(ms) {
      if (!ms) return '';
      return new Date(ms).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    },

    async loadFiles() {
      this.loading = true;
      this.error = null;
      try {
        const res = await this.gatewayClient.request('agents.files.list', { agentId: this.agentId });
        this.files = res?.files || [];
      } catch (e) {
        this.error = '加载文件列表失败: ' + e.message;
      } finally {
        this.loading = false;
      }
    },

    async selectFile(f) {
      this.activeFile = f.name;
      if (Object.hasOwn(this.contents, f.name)) return; // already loaded
      if (f.missing) {
        this.$set(this.contents, f.name, '');
        this.$set(this.drafts, f.name, '');
        return;
      }
      this.loading = true;
      try {
        const res = await this.gatewayClient.request('agents.files.get', {
          agentId: this.agentId,
          name: f.name,
        });
        const content = res?.file?.content ?? '';
        this.$set(this.contents, f.name, content);
        if (!Object.hasOwn(this.drafts, f.name)) {
          this.$set(this.drafts, f.name, content);
        }
      } catch (e) {
        this.$message.error('读取文件失败: ' + e.message);
      } finally {
        this.loading = false;
      }
    },

    onInput(e) {
      this.$set(this.drafts, this.activeFile, e.target.value);
    },

    async save() {
      this.saving = true;
      try {
        const dirtyFiles = Object.keys(this.drafts).filter(n => this.drafts[n] !== (this.contents[n] ?? ''));
        for (const name of dirtyFiles) {
          await this.gatewayClient.request('agents.files.set', {
            agentId: this.agentId,
            name,
            content: this.drafts[name],
          });
          this.$set(this.contents, name, this.drafts[name]);
        }
        this.$message.success('保存成功');
        await this.loadFiles(); // refresh metadata
      } catch (e) {
        this.$message.error('保存失败: ' + e.message);
      } finally {
        this.saving = false;
      }
    },

    revert() {
      for (const name of Object.keys(this.drafts)) {
        this.$set(this.drafts, name, this.contents[name] ?? '');
      }
    },
  },
  mounted() {
    this.loadFiles();
  },
};
</script>

<style lang="scss" scoped>
.agent-files-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #fafbfc;
}
.back-btn {
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 14px;
  &:hover { background: #f3f4f6; }
}
.panel-title {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 15px;
}
.agent-emoji { font-size: 20px; }
.agent-id { font-size: 12px; color: #9ca3af; font-weight: 400; }

.panel-loading, .panel-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 12px;
  color: #6b7280;
}

.panel-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* File tabs - vertical sidebar */
.file-tabs {
  width: 180px;
  min-width: 180px;
  border-right: 1px solid #e5e7eb;
  overflow-y: auto;
  background: #f9fafb;
  padding: 8px 0;
}
.file-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 13px;
  color: #374151;
  position: relative;
  &:hover { background: #f3f4f6; }
  &.active { background: #eef2ff; color: #4f46e5; font-weight: 500; }
  &.missing { opacity: 0.5; }
}
.file-icon { font-size: 14px; }
.file-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dirty-dot { color: #f59e0b; font-size: 10px; }

/* Editor */
.editor-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.editor-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #9ca3af;
  gap: 8px;
  .placeholder-icon { font-size: 32px; }
}
.editor-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 14px;
  border-bottom: 1px solid #e5e7eb;
  background: #fafbfc;
  font-size: 12px;
}
.editor-filename { font-weight: 600; color: #374151; }
.editor-meta { color: #9ca3af; }
.editor-textarea {
  flex: 1;
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  padding: 14px;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 13px;
  line-height: 1.7;
  color: #1f2937;
  background: #fff;
  tab-size: 2;
}
</style>
